import { buildMeasureAccessor, buildTimestampAccessor } from '../features/metricValue';
import { buildColorAccessor, MetricsGrey, MetricsNeutralColor } from '../features/status';
import { buildFirstThresholdMeasureAccessor } from '../features/threshold';
import { DataRow, EffectiveDefinition, ValueAccessor } from '../models';
import { COLOR_INDICATORS_FLAG, CONSTANT_COMPARISONS_FLAG, FeatureFlags } from './FeatureFlags';
/**
 * Vision:
 *
 * interface CardModel {
 *   indicator: StatusIndicatorBarModel;
 *   ban: CardBanModel;
 *   delta: CardDeltaModel;
 *   sparkline: SparklineModel;
 *   label: CardLabelModel;
 * }
 *
 * Sparkline will be the most complex by far. It is also a publicly supported
 * component included in VPC outside of the card, so we need to be aware that
 * changes to the Sparkline Props are technically breaking.
 *
 * For the moment, the functions in this module are extracted opportunistically
 * from our widgets module.
 *
 */
export interface CardModel {}

export const buildCardAccessors = (definition: EffectiveDefinition, featureFlags: FeatureFlags): CardAccessors => {
  const refLineEnabled = featureFlags[CONSTANT_COMPARISONS_FLAG] ?? true;
  const measureAccessor = buildMeasureAccessor(definition);
  const timestampAccessor = buildTimestampAccessor(definition);
  const comparisonAccessor = refLineEnabled ? buildFirstThresholdMeasureAccessor(definition) : undefined;

  const accessors = { measureAccessor, timestampAccessor, comparisonAccessor };
  return accessors;
};

interface CardAccessors {
  readonly measureAccessor: ValueAccessor<number>;
  readonly timestampAccessor: ValueAccessor<Date | undefined>;
  readonly comparisonAccessor: ValueAccessor<number | undefined> | undefined;
}

interface CardColors {
  readonly deltaColor: string;
  readonly dotColor: string;
  readonly lineColor: string;
}

export function buildCardColors(
  definition: EffectiveDefinition,
  latestValue: DataRow | undefined,
  featureFlags: FeatureFlags
): CardColors {
  const colorIndicatorsEnabled = featureFlags[COLOR_INDICATORS_FLAG];
  const colorAccessor = buildColorAccessor(definition);
  const colorForPoint = (latestValue && colorAccessor(latestValue)) ?? MetricsNeutralColor;
  const dotColor = colorIndicatorsEnabled ? colorForPoint : MetricsNeutralColor;
  const deltaColor = colorIndicatorsEnabled ? colorForPoint : MetricsGrey;
  const lineColor = colorIndicatorsEnabled ? MetricsGrey : MetricsNeutralColor;
  return { deltaColor, dotColor, lineColor };
}
